import { useMemo } from 'react';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { useAtom } from 'jotai';

import { Button as ButtonV2, Heading } from '@restworld/ui-ds-base';
import { cn, customImageLoader } from '@restworld/utils-common';
import { JobApplicationStatusKey } from '@restworld/utility-types';

import { useKanbanRowsAndMatchStatusKeys } from '../../../hooks';
import { candidateModalVariantAtom, selectedJobApplicationAtom } from '../../../employer-job-listing.atoms';

function CandidatePresentationNote() {
  const [selectedJobApplication] = useAtom(selectedJobApplicationAtom);
  const [candidateModalVariant] = useAtom(candidateModalVariantAtom);
  const isSpontaneousApplication = useMemo(
    () => !selectedJobApplication?.job_position_id,
    [selectedJobApplication?.job_position_id]
  );

  const candidateNotesProps = useMemo(
    () => ({
      jobApplicationStatusKey: selectedJobApplication?.status_key,
      discardedFromStatusKey: selectedJobApplication?.discarded_from_status_key,
      discardedBy: selectedJobApplication?.discarded_by,
      discardedOtherReason: selectedJobApplication?.discarded_other_reason,
      discardedReason: selectedJobApplication?.discarded_reason_var?.label,
      notes: selectedJobApplication?.worker_presentation?.presentation || '',
      csmImage: selectedJobApplication?.is_contacted_by_rw_member?.icon_url,
      coverLetter: selectedJobApplication?.cover_letter,
      evaluationAnalysis: selectedJobApplication?.evaluation?.evaluation_analysis,
      addedToShortlistAt: selectedJobApplication?.added_to_shortlist_at,
    }),
    [
      selectedJobApplication?.added_to_shortlist_at,
      selectedJobApplication?.cover_letter,
      selectedJobApplication?.discarded_by,
      selectedJobApplication?.discarded_from_status_key,
      selectedJobApplication?.discarded_other_reason,
      selectedJobApplication?.discarded_reason_var?.label,
      selectedJobApplication?.evaluation?.evaluation_analysis,
      selectedJobApplication?.is_contacted_by_rw_member?.icon_url,
      selectedJobApplication?.status_key,
      selectedJobApplication?.worker_presentation?.presentation,
    ]
  );
  const isDiscarded = useMemo(
    () =>
      candidateNotesProps?.jobApplicationStatusKey === 'worker_jp_discarded' ||
      // Treat pre-shortlist self-withdrawals as part of the "preselection rejected" view (Non idonei tab)
      (candidateNotesProps?.jobApplicationStatusKey === 'worker_jp_not_interested' &&
        !candidateNotesProps?.addedToShortlistAt),
    [candidateNotesProps?.addedToShortlistAt, candidateNotesProps?.jobApplicationStatusKey]
  );
  const isSpontaneousCandidate = useMemo(
    () => candidateModalVariant === 'spontaneous_candidate' || candidateModalVariant === 'spontaneous_candidate_notes',
    [candidateModalVariant]
  );

  return isSpontaneousCandidate || isSpontaneousApplication ? (
    <CoverLetter {...candidateNotesProps} />
  ) : isDiscarded ? (
    <DiscardedCandidateNote {...candidateNotesProps} />
  ) : (
    <ActiveShortListedCandidateNote {...candidateNotesProps} />
  );
}
export default CandidatePresentationNote;

function ActiveShortListedCandidateNote({ evaluationAnalysis, csmImage }: CandidateNotesProps) {
  return (
    <MainWrapper>
      <ImageAndTitleWrapper>
        {csmImage && (
          <ImageWrapper>
            <Image src={csmImage} alt="" fill loader={customImageLoader} className="object-cover object-center" />
          </ImageWrapper>
        )}
      </ImageAndTitleWrapper>
      <NoteAndButtonWrapper>
        <EvaluationWrapper>
          <EvaluationContent dangerouslySetInnerHTML={{ __html: evaluationAnalysis }} />
        </EvaluationWrapper>
      </NoteAndButtonWrapper>
    </MainWrapper>
  );
}

function DiscardedCandidateNote({
  discardedFromStatusKey,
  discardedBy,
  discardedOtherReason,
  discardedReason,
  evaluationAnalysis,
  addedToShortlistAt,
}: CandidateNotesProps) {
  const { t: t } = useTranslation('job-positions-page');

  const kanbanRowStatusKeys = useKanbanRowsAndMatchStatusKeys();

  const kanbanRowWhichCandidateWasIn = useMemo(
    () =>
      kanbanRowStatusKeys.find((v) => v.matchStatusKey.includes(discardedFromStatusKey as JobApplicationStatusKey))
        ?.title,
    [discardedFromStatusKey, kanbanRowStatusKeys]
  );

  const isPreselectionRejected = !addedToShortlistAt;

  if (isPreselectionRejected) {
    // For preselection-rejected candidates we only render the grey box when the AI
    // actually produced an analysis (Cases 1 + 2). For all the other variants
    // (withdrew, preassessment incomplete with no analysis, hard-rule rejection)
    // the contextual reason is surfaced via the orange alert in `ConstraintAlerts`
    // — showing an empty box would just add noise.
    if (!evaluationAnalysis) return null;

    return (
      <MainWrapper variant="discarded">
        <EvaluationContent dangerouslySetInnerHTML={{ __html: evaluationAnalysis }} />
      </MainWrapper>
    );
  }

  return (
    <MainWrapper variant="discarded">
      {!!kanbanRowWhichCandidateWasIn && (
        <div className="mb-3">
          <Heading size={6} weight="medium" color="muted" className="text-sm">
            {t('was_in')}
          </Heading>
          <Heading size={6} weight="medium" color="neutral" className="text-sm">
            {kanbanRowWhichCandidateWasIn}
          </Heading>
        </div>
      )}
      {!!discardedBy && (
        <div className="mb-3">
          <Heading size={6} weight="medium" color="muted" className="text-sm">
            {t('discarded_by')}
          </Heading>
          <Heading size={6} weight="medium" color="neutral" className="text-sm">
            {discardedBy.split('_').join(' ')}
          </Heading>
        </div>
      )}
      <div>
        <Heading size={6} weight="medium" color="muted" className="text-sm">
          {t('reason')}
        </Heading>
        <Heading size={6} weight="bold" color="neutral" className="text-sm">
          {discardedReason}
        </Heading>
        {!!discardedOtherReason && (
          <Heading size={6} weight="medium" color="neutral" className="text-sm">
            {discardedOtherReason}
          </Heading>
        )}
      </div>
    </MainWrapper>
  );
}

function CoverLetter({ coverLetter }: CandidateNotesProps) {
  const { t: t } = useTranslation();

  return (
    <MainWrapper variant="spontaneous">
      <NoteAndButtonWrapper>
        <EvaluationWrapper>
          {coverLetter ? coverLetter : t('common:this_candidate_did_not_upload_a_cover_letter_with_the_application')}
        </EvaluationWrapper>
      </NoteAndButtonWrapper>
    </MainWrapper>
  );
}

const MainWrapper = ({
  variant,
  className,
  ...props
}: { variant?: 'spontaneous' | 'discarded' | 'default' } & React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'rounded-lg px-4 py-3 lg:mx-0 min-h-[6.5rem] overflow-hidden overflow-y-auto',
      variant === 'discarded' ? 'bg-neutral-30' : 'bg-purple-50',
      className
    )}
    {...props}
  />
);
const ImageAndTitleWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('mb-4 flex gap-2 items-center', className)} {...props} />
);
const ImageWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'border border-purple-700 rounded-full bg-purple-300 relative w-8 h-8 min-w-[2rem] flex items-center justify-center object-cover object-center overflow-hidden',
      className
    )}
    {...props}
  />
);
const EvaluationWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('text-neutral-700 text-base w-full', className)} {...props} />
);
const NoteAndButtonWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col items-center gap-4', className)} {...props} />
);
const EvaluationContent = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'font-light text-base',
      '[&_h4]:text-base [&_h4]:font-bold [&_h4]:italic [&_h4]:mt-5 [&_h4]:mb-2 [&_h4:first-child]:mt-0',
      '[&_ul]:list-disc [&_ul]:list-outside [&_ul]:ml-5 [&_ul]:mb-4 [&_ul:last-child]:mb-4',
      '[&_strong]:font-medium',
      className
    )}
    {...props}
  />
);

interface CandidateNotesProps {
  evaluationAnalysis?: string;
  discardedFromStatusKey?: string;
  discardedBy?: string;
  discardedReason?: string;
  discardedOtherReason?: string;
  jobApplicationStatusKey?: JobApplicationStatusKey;
  csmImage?: string;
  coverLetter?: string;
  addedToShortlistAt?: string | null;
}
